import type { IconSource } from '@shopify/polaris';

import { Box, Collapsible, InlineStack, Icon, Text, BlockStack } from '@shopify/polaris';
import { CollapseIcon } from '~/presentation/icons/components/CollapseIcon';
import { ExpandIcon } from '~/presentation/icons/components/ExpandIcon';
import { DeleteIcon } from '@shopify/polaris-icons';
import { useToggle } from '~/hooks/useToggle';
import { GButton } from '~/presentation/ui';

interface GCardFilterProps {
  title: string;
  icon: IconSource;
  hideRemove?: boolean;
  minWidth?: string;
  onRemove: () => void;
  children: React.ReactNode;
}

export const GCardFilter = ({
  title,
  icon,
  hideRemove,
  minWidth = '448px',
  onRemove,
  children,
}: GCardFilterProps) => {
  const { value: isCollapsed, toggle: toggleCollapse } = useToggle(false);

  return (
    <Box
      background="bg-surface-secondary"
      padding="200"
      borderRadius="200"
      width="100%"
      minWidth={minWidth}
    >
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="100" blockAlign="center">
            <Icon source={icon} />

            <Text as="p" variant="bodyMd" fontWeight="medium">
              {title}
            </Text>
          </InlineStack>

          <InlineStack blockAlign="start">
            {!hideRemove && (
              <GButton
                inset
                icon={DeleteIcon}
                variant="tertiary"
                tone="critical"
                stopPropagation
                accessibilityLabel={`Remove ${title} filter`}
                onClick={onRemove}
              />
            )}

            <GButton
              inset
              icon={isCollapsed ? ExpandIcon : CollapseIcon}
              variant="tertiary"
              tooltip={{
                content: isCollapsed ? 'Expand' : 'Collapse',
              }}
              onClick={toggleCollapse}
            />
          </InlineStack>
        </InlineStack>

        <Collapsible
          id={`filter-section-${title.toLowerCase().replace(' ', '-')}`}
          open={!isCollapsed}
          transition={{
            duration: 'var(--p-motion-duration-150)',
            timingFunction: 'var(--p-motion-ease-in-out)',
          }}
        >
          {children}
        </Collapsible>
      </BlockStack>
    </Box>
  );
};
